import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Typography,
} from "@atlas/core";
import { useTranslation } from "@atlas/sdk";
import type { IHomeLaunchpadRecentlyUsedSolution } from "@models";
import { useEffect, useMemo } from "react";
import {
  HOME_SOLUTIONS_GRID_CLASS,
  RECENTLY_USED_SECTION_KEY,
} from "./homeSolutions.constants";
import { SolutionLaunchCard } from "./SolutionLaunchCard";

interface IRecentlyUsedSolutionsSectionProps {
  recentlyUsedSolutions: IHomeLaunchpadRecentlyUsedSolution[];
  searchQuery: string;
  isLaunchPending: boolean;
  launchVariables?: { launchUrl: string; solutionId?: string };
  onFilteredCountChange: (appKey: string, count: number) => void;
  onLaunchApp: (launchUrl: string, solutionId?: string) => void;
}

export function RecentlyUsedSolutionsSection({
  recentlyUsedSolutions,
  searchQuery,
  isLaunchPending,
  launchVariables,
  onFilteredCountChange,
  onLaunchApp,
}: IRecentlyUsedSolutionsSectionProps) {
  const { t } = useTranslation();

  const lowerQuery = searchQuery.trim().toLowerCase();

  const filteredSolutions = useMemo(() => {
    if (!lowerQuery) {
      return recentlyUsedSolutions;
    }

    return recentlyUsedSolutions.filter(({ solution, applicationLabel }) => {
      const solutionTitle = solution.label || solution.name || "";
      return (
        solutionTitle.toLowerCase().includes(lowerQuery) ||
        applicationLabel.toLowerCase().includes(lowerQuery)
      );
    });
  }, [lowerQuery, recentlyUsedSolutions]);

  const isVisible = filteredSolutions.length > 0;

  useEffect(() => {
    onFilteredCountChange(
      RECENTLY_USED_SECTION_KEY,
      isVisible ? filteredSolutions.length : 0,
    );
  }, [filteredSolutions.length, isVisible, onFilteredCountChange]);

  if (!isVisible) {
    return null;
  }

  return (
    <AccordionItem value={RECENTLY_USED_SECTION_KEY}>
      <AccordionTrigger>
        <Typography.H4 className="truncate text-base">
          {t("hub.home.recently_used", { defaultValue: "Recently Used" })}
        </Typography.H4>
      </AccordionTrigger>

      <AccordionContent>
        <div className={HOME_SOLUTIONS_GRID_CLASS}>
          {filteredSolutions.map(({ solution, launchUrl }) => (
            <SolutionLaunchCard
              key={`recent-${solution.solutionId}`}
              solutionData={solution}
              title={solution.label || solution.name}
              launchUrl={launchUrl}
              launchDisabled={isLaunchPending || !launchUrl}
              isLaunching={
                isLaunchPending &&
                launchVariables?.launchUrl === launchUrl &&
                launchVariables?.solutionId === solution.solutionId
              }
              onLaunchApp={onLaunchApp}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
